import data from "../../shared/sharedData";

const fieldsToClear = [
  "appAExpAddress1",
  "appAExpAddress2",
  "appAExpCity",
  "appAExpPostalCode",
  "appAExpCountryId",
  "appAExpStateTerritory",
  "appAExpProvince",
  "appAExpJobTitle",
  "appAExpStartDate",
  "appAExpEndDate",
  "appAExpFWPresent",
  "appAExpWorkHour",
  "appAExpJobDuties",
];

export default {
  name: "Appendix A.E - Foreign Worker Work Experience",
  pageInfo: {
    text: "<b><u>Note:</u></b> This section is required if there are experience requirements for the job opportunity.  Identify any relevant employment experiences (other than training) that qualify the foreign worker for the job opportunity for which the employer is seeking permanent labor certification.  This may include, but is not limited to, paid and unpaid experience, internships, apprenticeships, etc.  List the most recent experience first.  This section will collect one (1) set of information related to work experience.  For electronic filings, if the employer needs to disclose more sections of work experience, the filing system will automatically provide the employer with the option of adding more sets until all required responses are completed.  For mailed or paper filings, the employer will make one or more copies of the work experience set E.a of the Appendix to complete and attach to the Form ETA-9089.<b><i> The employer must complete every field for each set and submission of additional work experience information in any other form or format will not be accepted.</i></b><br/><br/><b><u>DO NOT</u></b> include contact information (e.g., telephone number, email address) of the employer and the name(s) of the foreign worker's supervisor(s) or manager(s).<br/><br/><b><u>DO NOT</u></b> include periods of unemployment.",
    coloring: "grey",
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        isVisible: {
          when: "appAExpEmpName",
          is: ["N/A", "n/a", "n/A", "N/a"],
          operator: "compareFieldValuesModal",
          modal: "fwExpAppE",
        },
        title: "Section Complete",
        description:
          "You marked N/A, indicating that the foreign worker does not have the relevant U.S. Work Experience or foreign equivalent. To add a work experience requirement, please update 'N/A' to the actual U.S. U.S. Work Experience or foreign equivalent.",
        type: "info",
      },
    ],
  },
  sections: [
    {
      code: "Appendix A.E",
      machine_name: "fwExpAppE",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Foreign Worker Work Experience",
      modalHeader: "Add Foreign Worker Work Experience",
      sectionHeader: "Foreign Worker Work Experience",
      listType: "table",
      hide: (formApi) => {
        const fieldArray = formApi.getState().values.fwExpAppE;
        for (var i in fieldArray) {
          const data = fieldArray[i]?.appAExpEmpName;
          if (
            data === "N/A" ||
            data === "n/a" ||
            data === "n/A" ||
            data === "N/a"
          )
            return true;
        }
      },
      validate: [
        {
          tableRequiredIf: "appendixAERequired",
          message: "Please enter in at least one row.",
          needsFieldValues: true,
          extraData: {
            valueOf: true,
          },
        },
      ],
      editButton: true,
      removeLink: "Remove Foreign Worker Work Experience",
      modalFields: [
        {
          code: "1",
          machine_name: "appAExpEmpName",
          label: "Employer Name",
          widget: "text",
          tableHeader: "Employer Name",
          validate: [
            {
              isRequired: "appendixAERequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: true,
              },
            },
          ],
          parent_machine_name: "add_values_field",
          clearSchemaIfNA: fieldsToClear,
          maxLength: 120,
          tooltip:
            "Enter the full legal name of the business, person, association, firm, corporation, or organization, i.e., the employer that employed the foreign worker.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
        {
          code: "1.a",
          machine_name: "appAExpAddress1",
          label: "Address 1",
          widget: "text",
          hideOnTable: true,
          validate: [
            "required",
            {
              minLength: 2,
              message: `Address 1 must contain at least 2 characters.`,
            },
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 60,
          tooltip:
            "Enter the street address of the employer identified in question 1.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
        {
          code: "1.b",
          machine_name: "appAExpAddress2",
          label: "Address 2",
          widget: "text",
          hideOnTable: true,
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 60,
          tooltip:
            "Enter additional information about the street address of the employer identified in question 1.  If the foreign worker does not have any relevant work experience, or no additional space is needed, enter “N/A.”",
        },
        {
          code: "1.c",
          machine_name: "appAExpCity",
          label: "City or Town",
          widget: "text",
          hideOnTable: true,
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 35,
          tooltip:
            "Enter the city or town of the employer identified in question 1.  If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
        {
          code: "1.d",
          machine_name: "appAExpPostalCode",
          label: "Postal Code",
          widget: "text",
          hideOnTable: true,
          validate: [
            {
              isRequired: "appAExpCountryId",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
                isInlineModal: true,
              },
            },
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer identified in question 1.  If the foreign worker does not have any relevant work experience, or the postal code is not known or does not exist, leave blank.",
        },
        {
          code: "1.e",
          machine_name: "appAExpCountryId",
          label: "Country",
          tableHeader: "Country",
          widget: "select",
          isInlineModal: true,
          clearSchema: ["appAExpStateTerritory", "appAExpProvince"],
          selection: data.country,
          validate: ["required"],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          tooltip: `Enter the country of the employer identified in question 1.  If the city and country
             are the same, the name <u>must</u> still be entered in <u>both</u> fields.  If the foreign
              worker does not have any relevant work experience, enter “N/A.”`,
        },
        {
          code: "1.f",
          label: "State, Territory, or Province",
          machine_name: "appAExpStateTerritory",
          widget: "select",
          selection: data.state,
          state: true,
          hideOnTable: true,
          maxLength: 35,
          validate: ["required"],
          isVisible: {
            when: (formApi) => {
              const countryCheck = ["UNITED STATES OF AMERICA"];
              const country =
                formApi.getState().values.add_values_field?.[0]
                  ?.appAExpCountryId;
              const neededValue =
                formApi.getState().values.add_values_field?.[0]?.appAExpEmpName;
              if (!!neededValue && countryCheck.includes(country)) {
                return true;
              }
              return false;
            },
            is: "fwStateSelectCheck",
            operator: "customValidation",
          },
          tooltip:
            "Enter the state, territory, or province of the employer identified in question 1.  If the foreign worker does not have any relevant work experience, or no state, territory, or province exists, enter “N/A.”",
        },
        {
          code: "1.f",
          label: "State, Territory, or Province",
          machine_name: "appAExpProvince",
          widget: "text",
          hideOnTable: true,
          maxLength: 35,
          validate: ["required", { minLength: 2 }],
          isVisible: {
            when: (formApi) => {
              const countryCheck = [
                "UNITED STATES OF AMERICA",
                "- Select -",
                null,
                undefined,
              ];
              const country =
                formApi.getState().values.add_values_field?.[0]
                  ?.appAExpCountryId;
              const neededValue =
                formApi.getState().values.add_values_field?.[0]?.appAExpEmpName;
              if (!!neededValue && !countryCheck.includes(country)) {
                return true;
              }
              return false;
            },
            is: "fwStateTextCheck",
            operator: "customValidation",
          },
          tooltip: `Enter the state, territory, or province of the employer identified
             in question 1.  If the foreign worker does not have any relevant
              work experience, or no state, territory, or province exists, enter “N/A.”`,
        },
        {
          code: "1.g",
          machine_name: "appAExpJobTitle",
          label: "Job Title",
          widget: "text",
          tableHeader: "Job Title",
          validate: ["required"],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 60,
          tooltip:
            "Enter the foreign worker’s job title with the employer identified in question 1.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
        {
          code: "1.h",
          machine_name: "appAExpStartDate",
          label: "Start Date",
          hideOnTable: true,
          widget: "text",
          maxLength: 7,
          parse: "monthYear",
          validate: [
            "required",
            {
              monthYear: "appAExpStartDate",
              message: "mm/yyyy format required",
            },
            {
              monthYearStartEnd: "appAExpEndDate",
              message: "Start Date must occur before End Date.",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "start",
              },
            },
            {
              validMonth: "appAExpStartDate",
              message: "a valid month is required",
            },
            "noFutureDateTextFields",
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter the month and year the foreign worker started working for the employer identified in question 1.  Use a MM/ YYYY format.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
        {
          code: "1.i",
          machine_name: "appAExpEndDate",
          label: "End Date",
          hideOnTable: true,
          widget: "text",
          maxLength: 7,
          parse: "monthYear",
          validate: [
            "required",
            {
              monthYear: "appAExpEndDate",
              message: "mm/yyyy format required",
            },
            {
              monthYearStartEnd: "appAExpStartDate",
              message: "End Date must occur after Start Date.",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "end",
              },
            },
            {
              validMonth: "appAExpEndDate",
              message: "a valid month is required",
            },
            "noFutureDateTextFields",
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter the month and year the foreign worker stopped working or ended employment with the employer identified in question 1.  Use a MM/YYYY format.  If the foreign worker does not have any relevant work experience, leave blank.",
        },
        {
          hideOnTable: true,
          isGroup: {
            code: "1.j",
            machine_name: "appAExpFWPresent",
            required: true,
            label: "Present",
            isVisible: {
              when: "appAExpEmpName",
              is: "useless", // useless field, needed to fire isVisible cond
              operator: "has_value_and_not_na",
            },
            clearBtn: {
              clearSchema: [""],
            },
            tooltip:
              "Mark “Yes” to indicate that the foreign worker is currently working for the employer identified in question 1.  If the foreign worker is <u>not</u> currently working with the employer identified in question 1, mark “No.”  If the foreign worker does not have any relevant work experience, leave blank.",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "Yes",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "No",
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "1.k",
          machine_name: "appAExpWorkHour",
          label: "Hours Worked Per Week",
          widget: "text",
          hideOnTable: true,
          validate: ["number"],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          maxLength: 3,
          tooltip:
            "Enter the normal number of hours the foreign worker worked per week while working for the employer identified in question 1.  If the foreign worker does not have any relevant work experience, leave blank.",
        },
        {
          code: "1.l",
          machine_name: "appAExpJobDuties",
          label: `Job Duties: Specify details of the job work (work tasks performed, use of 
            tools/equipment, supervision, etc.) (up to 3,500 characters)`,
          widget: "text_area",
          hideOnTable: true,
          validate: [
            "required",
            {
              minLength: 5,
            },
          ],
          isVisible: {
            when: "appAExpEmpName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          minLength: 5,
          inputLimit: 3500,
          tooltip:
            "Enter a description of the job duties the foreign worker performed while working for the employer identified in question 1.  Include work done, managerial or supervisory functions performed, equipment and tools used, materials or products handled, and all other relevant information (up to 3,500 characters).  <b><i>The description must be completed in the space provided</i></b>.  If the foreign worker does not have any relevant work experience, enter “N/A.”",
        },
      ],
    },
  ],
};
